import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { produce } from 'immer';
import { toast } from 'react-toastify';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Map, useControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Button, Container, Paper, Typography, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowBackIos, Delete as DeleteIcon } from '@mui/icons-material';

import { warehouseUserState } from 'atoms/WarehouseUserState';
import { warehouseDetailUsersState } from 'atoms/WarehouseState';
import { warehousePrinterState } from 'atoms/WarehousePrinterState';
import { warehouseConfigState, warehouseObjState } from 'atoms/WarehouseObjState';
import { toggleEditWarehouse, toggleEditWarehouseConfig } from 'atoms/ToggleEditWarehouse';

import Delete from 'components/Dialog/Delete';
import {
  deleteWarehouse,
  detailWarehouse,
  listDsp,
  listWarehousePrinters,
  listWarehouseUsers,
} from 'repositories/HttpRepository';
import { useWarehouseConfigLoad } from 'hooks/Warehouse';

import { processType } from './../helpers';
import EditWarehouse from './edit';
import PrinterInfo from './Info/printerInfo';
import UserInfo from './Info/userInfo';
import Locations from './Info/Locations';
import StorageAreas from './Info/StorageAreas';
import useMapLayers from './MapLayer';
import { pickupAddressState } from './States';
import EditWarehouseConfig from './EditWarehouseConfig';
import { CONFIG_FIELDS, TRANSITION_FIELDS } from 'constants/warehouseConfig';
import { dspState } from 'atoms/DspState';

function DeckGLOverlay(props) {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

const useStyles = makeStyles()((theme) => ({
  LeftInfo: {
    backgroundColor: '#fff',
    marginBottom: 20,
    '& .text-secondary': {
      color: '#5a5a5a',
      fontWeight: 600,
    },
  },
  RightInfo: {
    '& >div': {
      backgroundColor: '#e4e4e4',
      padding: 0,
    },
  },
  Main: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: theme.spacing(4, 0),

    '& span': {
      color: '#8d8d8d',
    },
  },
  paper: {
    padding: theme.spacing(3, 4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: '#fff',

    '& h4': {
      fontWeight: 'bold',
      fontSize: 22,
    },
  },
  Info: {
    padding: theme.spacing(2),
    margin: theme.spacing(0, 4),
    color: '#5a5a5a',
    fontWeight: 600,
    borderRadius: 5,
    backgroundColor: '#f6f6f6',
    marginBottom: theme.spacing(3),

    '& h5': {
      fontWeight: 'bold',
      fontSize: 20,
    },

    '& div': {
      lineHeight: '30px',
    },

    '& .info-header': {
      display: 'flex',
      justifyContent: 'space-between',

      '& button': {
        boxShadow: 'none',
      },
    },
  },
  InfoLabel: {
    color: '#000',
    fontSize: 14,
    fontFamily: 'AvenirNext',
  },
  InfoValue: {
    fontSize: 12,
    wordBreak: 'break-word',
  },
  InfoRow: {
    padding: theme.spacing(1, 0),
  },

  DeleteBtn: {
    backgroundColor: '#c82f42 !important',

    '& span': {
      color: '#ffffff !important',
    },
    color: 'white !important',
  },
  markerImage: {
    width: 50,
    height: 50,
  },
  redText: {
    color: 'red',
  },
  transitionTitle: {
    fontSize: 16,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  transitionContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    columnGap: 10,
    marginTop: 10,
  },
  transitionItem: {
    marginBottom: 20,
  },
  transitionItemTitle: {
    fontSize: 14,
    color: '#000',
    fontWeight: 500,
  },
  transitionItemValue: {
    fontSize: 12,
  },
}));

function DetailWarehouse() {
  const mapStyle = `${process.env.REACT_APP_MAP_TILER_URI}?key=${process.env.REACT_APP_MAP_TILER_KEY}`;
  const layers = useMapLayers();
  const { classes } = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();

  const [warehouse, setWarehouse] = useRecoilState(warehouseObjState);
  const warehouseConfig = useRecoilValue(warehouseConfigState);
  const [onEditWarehouse, setOnEditWarehouse] = useRecoilState(toggleEditWarehouse);
  const [editWhConfig, setEditWhConfig] = useRecoilState(toggleEditWarehouseConfig);
  const [onDeleteWarehouse, setOnDeleteWarehouse] = useState(false);
  const [users, setUsers] = useRecoilState(warehouseUserState);
  const [printers, setPrinters] = useRecoilState(warehousePrinterState);
  const [viewport, setViewport] = useState({});
  const userOrder = useRecoilValue(warehouseDetailUsersState);
  const [pickupAddress, setPickupAddress] = useRecoilState(pickupAddressState);
  const [dspList, setDspList] = useRecoilState(dspState);
  const { configLoaded, loadFailed } = useWarehouseConfigLoad(id);

  const { transitions = [] } = warehouseConfig;

  useEffect(() => {
    detailWarehouse(id).then((resp) => {
      if (resp.ok) {
        setWarehouse(resp.data);
        const address = resp.data.address;
        setPickupAddress({ lng: address.lng, lat: address.lat });
      }
    });
  }, []);

  useEffect(() => {
    listWarehouseUsers(id, userOrder).then((resp) => {
      if (resp.ok) {
        setUsers(resp.data.items);
      }
    });
  }, [userOrder]);

  useEffect(() => {
    listWarehousePrinters(id).then((resp) => {
      if (resp.ok) {
        setPrinters(resp.data.items);
      }
    });

    if (!dspList) {
      const defaultParams = { order_by: 'id', desc: false, size: 999 };
      listDsp(defaultParams)
        .then((resp) => {
          if (resp.ok) {
            setDspList(resp.data);
          }
        })
        .catch((err) => toast.error(err));
    }
  }, []);

  useEffect(() => {
    setViewport(
      produce(viewport, (draftState) => {
        draftState.zoom = radiusToZoom();
      }),
    );
  }, [pickupAddress.radius]);

  const radiusToZoom = () => {
    const { radius } = pickupAddress;
    const maxRadius = Math.max(...radius);
    return Math.round(14 - Math.log(maxRadius) / Math.LN2);
  };

  useEffect(() => {
    setViewport({
      latitude: isNaN(parseFloat(warehouse.address.lat)) ? 0 : parseFloat(warehouse.address.lat),
      longitude: isNaN(parseFloat(warehouse.address.lng)) ? 0 : parseFloat(warehouse.address.lng),
      zoom: 10,
    });
  }, [warehouse.address.lat, warehouse.address.lng]);

  function handleEdit(e) {
    setOnEditWarehouse(true);
  }

  function turnOnDelete() {
    setOnDeleteWarehouse(true);
  }

  function handleDelete(e) {
    deleteWarehouse(id).then((resp) => {
      if (resp.ok) {
        toast.success('Delete successfully !');
        navigate('/warehouses');
      } else {
        toast.error('Server is having problem, please try again later.');
      }
    });
  }

  function getDateTime(dateTime) {
    if (dateTime === null) {
      return;
    }

    let time = new Date(dateTime);
    let month = time.getMonth() + 1;
    return time.getHours() + ':' + time.getMinutes() + ', ' + month + '/' + time.getDate() + '/' + time.getFullYear();
  }

  function validateLatLng(data) {
    const lat = isNaN(parseFloat(data.lat)) ? 0 : parseFloat(data.lat);
    const lng = isNaN(parseFloat(data.lng)) ? 0 : parseFloat(data.lng);

    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  function renderConfigValue(field) {
    if (!warehouseConfig[field]) {
      return 'N/A';
    }

    return warehouseConfig[field].toString();
  }

  const currentDSP = dspList?.items?.find((dsp) => dsp.id === warehouse.owner_id) ?? {};

  return (
    <Container maxWidth={false}>
      <div className={classes.buttonWrapper}>
        <Button
          variant="contained"
          color="inherit"
          startIcon={<ArrowBackIos color="inherit" />}
          component={Link}
          to="/warehouses"
        >
          Back
        </Button>

        <Button
          variant="contained"
          color="secondary"
          className={classes.DeleteBtn}
          onClick={turnOnDelete}
          startIcon={<DeleteIcon />}
        >
          Remove Warehouse
        </Button>
      </div>
      <Grid container fullwidth="true" spacing={3} className={classes.Main}>
        <Grid
          size={{
            xs: 12,
            sm: 8,
          }}
        >
          <Box className={classes.LeftInfo}>
            <Paper className={classes.paper} elevation={0}>
              <Typography variant="h4" color="secondary">
                Warehouse Details
              </Typography>
              <Box>
                <span>Last updated:</span>
                <Box component="span" className="text-secondary">
                  {getDateTime(warehouse._updated)} by {warehouse.updated_by_str}
                </Box>
              </Box>
            </Paper>

            <Paper className={classes.Info} elevation={0}>
              <Box className="info-header">
                <Typography variant="h5" color="secondary">
                  Warehouse Info
                </Typography>
                <Button variant="contained" color="primary" onClick={handleEdit}>
                  Edit
                </Button>
              </Box>
              <Grid container spacing={3} fullwidth="true" className={classes.InfoRow}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Warehouse alias:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.alias}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Warehouse ID:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.id}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Type:</Typography>
                  <Typography className={classes.InfoValue}>
                    {processType(
                      warehouse.owner_type,
                      warehouse.client_name ?? currentDSP?.code ?? currentDSP?.company,
                    )}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={3} fullwidth="true" className={classes.InfoRow}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Address:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.address.street}</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.address.street2}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Lattitude:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.address.lat}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Longitude:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.address.lng}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Region:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.region}</Typography>
                </Grid>
              </Grid>

              <Grid container spacing={3} fullwidth="true" className={classes.InfoRow}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Access Code:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.access_code}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Pickup Instructions:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.pickup_note}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                >
                  <Typography className={classes.InfoLabel}>Return Instructions:</Typography>
                  <Typography className={classes.InfoValue}>{warehouse.return_note}</Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper className={classes.Info} elevation={0}>
              <Box className="info-header">
                <Typography variant="h5" color="secondary">
                  Warehouse Config
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!configLoaded || loadFailed || !warehouse?.id}
                  onClick={() => setEditWhConfig(true)}
                >
                  Edit
                </Button>
              </Box>
              {configLoaded && !loadFailed && _.isEmpty(warehouseConfig) ? (
                <Box className={classes.redText}>
                  Warehouse config must be setup in order to run properly. SETUP NOW &gt;&gt;&gt;
                </Box>
              ) : (
                <>
                  <Grid container spacing={3} fullwidth="true" className={classes.InfoRow}>
                    {CONFIG_FIELDS.map((row) => {
                      return (
                        <Grid
                          key={row.field}
                          size={{
                            xs: 12,
                            sm: 4,
                          }}
                        >
                          <Box className={classes.InfoLabel}>{row.label}</Box>
                          <Box className={classes.InfoValue}>{renderConfigValue(row.field)}</Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {transitions.length > 0 && (
                    <>
                      <p className={classes.transitionTitle}>Transitions</p>
                      <div className={classes.transitionContainer}>
                        {transitions.map((item) =>
                          TRANSITION_FIELDS.map((field) => (
                            <div key={field.label} className={classes.transitionItem}>
                              <Typography className={classes.InfoLabel}>{field.label}</Typography>
                              <Typography className={classes.InfoValue}>
                                {(field.type === 'array' ? item[field.dataIndex]?.join(', ') : item[field.dataIndex]) ||
                                  'N/A'}
                              </Typography>
                            </div>
                          )),
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </Paper>

            <Locations warehouse_id={id} region={warehouse.region} alias={warehouse.alias} />
            <StorageAreas warehouse_id={id} />
            <UserInfo datas={users} warehouse_id={id} />
            <PrinterInfo warehouse_id={id} />
          </Box>
        </Grid>
        <Grid
          className={classes.RightInfo}
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <Paper className={classes.paper}>
            <Map
              {...viewport}
              mapStyle={mapStyle}
              onMove={(e) => setViewport(e.viewState)}
              style={{
                width: '100%',
                height: '500px',
                border: '1px solid #ccc',
              }}
            >
              <DeckGLOverlay layers={layers} />
            </Map>
          </Paper>
        </Grid>
      </Grid>
      {onEditWarehouse && <EditWarehouse />}
      {/* Mounted conditionally on purpose: the editor snapshots the config into react-hook-form's
          defaultValues once, at mount. An always-mounted <Dialog open> would snapshot the atom's
          empty default at page load and save that over the stored config. */}
      {editWhConfig && <EditWarehouseConfig />}
      {onDeleteWarehouse && (
        <Delete
          labelCancel="NO"
          labelApprove="YES, remove warehouse"
          content={'Are you sure to remove ' + warehouse.alias + ' ?'}
          handleCancel={(e) => setOnDeleteWarehouse(false)}
          handleApprove={handleDelete}
        ></Delete>
      )}
    </Container>
  );
}

export default DetailWarehouse;
